import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { applySeo, SITE_ORIGIN } from "@/lib/seo";
import seoPages from "@/data/seoPages.json";

const HOME_DESCRIPTION =
  "QR menü ve dijital vitrin ile ürünlerinizi, hizmetlerinizi ve kampanyalarınızı tek bağlantıda sergileyin. QR kodunuzu paylaşın, içeriğinizi dilediğiniz an yönetin.";

const SEO_LANDING_PAGES = Object.fromEntries(
  Object.entries(seoPages).map(([key, page]) => [
    `/${key}`,
    {
      title: page.metaTitle,
      description: page.metaDescription,
      name: page.seoName,
    },
  ]),
);

const routeSeo = (pathname) => {
  if (pathname === "/") {
    return {
      title: "Web Menü | QR Menü, Dijital Menü ve Dijital Vitrin",
      description: HOME_DESCRIPTION,
      pathname,
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            "@id": `${SITE_ORIGIN}/#organization`,
            name: "Megriva",
            url: "https://megriva.com/",
          },
          {
            "@type": "WebSite",
            "@id": `${SITE_ORIGIN}/#website`,
            url: `${SITE_ORIGIN}/`,
            name: "Web Menü",
            inLanguage: "tr-TR",
            publisher: { "@id": `${SITE_ORIGIN}/#organization` },
          },
          {
            "@type": "SoftwareApplication",
            "@id": `${SITE_ORIGIN}/#software`,
            name: "Web Menü",
            url: `${SITE_ORIGIN}/`,
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web, iOS, Android",
            description: HOME_DESCRIPTION,
            publisher: { "@id": `${SITE_ORIGIN}/#organization` },
          },
        ],
      },
    };
  }

  const seoLanding = SEO_LANDING_PAGES[pathname];

  if (seoLanding) {
    const canonical = `${SITE_ORIGIN}${pathname}`;
    return {
      ...seoLanding,
      pathname,
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebPage",
            "@id": `${canonical}#webpage`,
            url: canonical,
            name: seoLanding.name,
            description: seoLanding.description,
            inLanguage: "tr-TR",
            isPartOf: { "@id": `${SITE_ORIGIN}/#website` },
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Web Menü",
                item: `${SITE_ORIGIN}/`,
              },
              {
                "@type": "ListItem",
                position: 2,
                name: seoLanding.name,
                item: canonical,
              },
            ],
          },
        ],
      },
    };
  }

  if (pathname === "/privacy_policy") {
    return {
      title: "Gizlilik Politikası | Web Menü",
      description: "Web Menü gizlilik politikası ve kişisel verilerin işlenmesine ilişkin bilgilendirme.",
      pathname,
    };
  }

  if (pathname === "/terms_of_use") {
    return {
      title: "Kullanım Koşulları | Web Menü",
      description: "Web Menü hizmetinin kullanım koşulları ve kullanıcı sözleşmesine ilişkin bilgiler.",
      pathname,
    };
  }

  const privatePrefixes = ["/admin", "/login", "/register", "/forgot-password"];
  const isPrivate = privatePrefixes.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

  if (isPrivate) {
    return {
      title: "Web Menü",
      description: "Web Menü işletme yönetim ekranı.",
      pathname,
      robots: "noindex,nofollow,noarchive",
    };
  }

  return {
    title: "Dijital Vitrin | Web Menü",
    description:
      "Web Menü ile işletmenin ürünlerini, hizmetlerini, kampanyalarını ve iletişim bilgilerini dijital vitrinde görüntüleyin.",
    pathname,
  };
};

export default function SeoRouteManager() {
  const location = useLocation();

  useEffect(() => {
    applySeo(routeSeo(location.pathname));
  }, [location.pathname]);

  return null;
}
